import { ConfirmLeavePage } from "@/components/commons/ConfirmLeavePage";
import { FCC } from "@/types/common";
import { getFirstErrorPath } from "@/utils/getFirstErrorPath";
import { cleanObject } from "@/utils/object";
import cloneDeep from "lodash/cloneDeep";
import isEqualWith from "lodash/isEqualWith";
import { createContext, ReactNode, useContext, useEffect, useRef, useState, useMemo } from "react";
import { FieldValues, FormProvider, UseFormReturn } from "react-hook-form";
import { BlockerFunction } from "react-router-dom";
import { ObjectSchema } from "yup";

type TFormProps<T extends FieldValues> = {
  methods: UseFormReturn<T, object, T>;
  onSubmit: (data: T) => void;
  shouldBlock?: boolean | BlockerFunction;
  enableBlocker?: boolean;
  children: ReactNode;
  schema: ObjectSchema<object>;
  /** Initial values for dirty comparison. Pass this when form data is loaded (e.g., edit mode) */
  initialValues?: Partial<T>;
};

export const Form = <T extends FieldValues>({
  methods,
  onSubmit,
  shouldBlock = false,
  enableBlocker = true,
  children,
  schema,
  initialValues,
}: TFormProps<T>) => {
  const ref = useRef<HTMLFormElement | null>(null);
  const initialValuesRef = useRef<Partial<T> | null>(null);
  const [isBlockedForm, setIsBlockedForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Destructure formState properties to subscribe to them (React Hook Form Proxy requirement)
  const {
    formState: { errors, isSubmitSuccessful },
    getValues,
    watch,
  } = methods;

  // Update initial values when prop changes (e.g., after data is fetched in edit mode)
  // Use cloneDeep to avoid reference issues (preserves Dayjs objects)
  useEffect(() => {
    if (initialValues !== undefined) {
      initialValuesRef.current = cloneDeep(initialValues);
    } else if (initialValuesRef.current === null) {
      initialValuesRef.current = cloneDeep(getValues());
    }
  }, [initialValues, getValues]);

  // Watch all fields to trigger re-render on changes
  const watchValue = watch();

  // Compare current values with initial values using deep equality
  useEffect(() => {
    // Skip blocking during submit
    if (isSubmitting) {
      setIsBlockedForm(false);
      return;
    }
    // Only skip blocking after actual successful submission (onSubmit completed)
    if (isSubmitSuccessful) {
      setIsBlockedForm(false);
      return;
    }
    if (initialValuesRef.current === null) {
      setIsBlockedForm(false);
      return;
    }

    const hasChanged = !isEqualWith(cleanObject(watchValue), cleanObject(initialValuesRef.current), (a, b) => {
      // Handle number/string type mismatch from HTML inputs (e.g., 100 vs "100")
      if (typeof a !== typeof b && (typeof a === "number" || typeof a === "string") && (typeof b === "number" || typeof b === "string")) {
        return String(a) === String(b);
      }
      return undefined; // fallback to default lodash deep comparison
    });

    setIsBlockedForm(hasChanged);
  }, [watchValue, isSubmitting, isSubmitSuccessful]);

  useEffect(() => {
    if (!ref.current) return;
    if (!errors) return;
    const firstErrorPath = getFirstErrorPath(errors);
    if (firstErrorPath) {
      const field = ref.current.querySelector(`[name="${firstErrorPath}"]`) as HTMLInputElement;
      field?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errors]);

  return (
    <FormProvider {...methods}>
      <FormSchemaProvider schema={schema}>
        {enableBlocker && <ConfirmLeavePage shouldBlock={shouldBlock || isBlockedForm} />}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setIsSubmitting(true);
            methods
              .handleSubmit(async (data) => {
                await onSubmit(data);
              })()
              .catch(() => {})
              .finally(() => setIsSubmitting(false)); // Handles both validation fail & API error
            e.stopPropagation();
          }}
          ref={ref}
        >
          {children}
        </form>
      </FormSchemaProvider>
    </FormProvider>
  );
};

const FormSchemaContext = createContext<Record<string, TFieldMeta>>({});

type TFormSchemaProviderProps = { schema: ObjectSchema<object> };

export type TFieldMeta = {
  label: string;
  required: boolean;
  name: string;
  type: string;
  meta: {
    type?: string;
    isZipCode?: boolean;
    isFurigana?: boolean;
    autoTransformToHalfWidth?: boolean;
    autoTransformToFullWidth?: boolean;
    autoTransformToUpperCase?: boolean;
    autoTransformToHalfWidthForMatchLicenseNumber?: boolean;
    removeSpaces?: boolean;
    katakanaToHiragana?: boolean;
    isPhone?: boolean;
    isEmptyToNull?: boolean;
  };
};

/** Yup field description shape returned by schema.describe() */
interface SchemaDesc {
  type?: string;
  label?: string;
  optional?: boolean;
  nullable?: boolean;
  meta?: TFieldMeta["meta"];
  fields?: Record<string, SchemaDesc>;
  innerType?: SchemaDesc;
}

/** Extract TFieldMeta from a yup field description */
const descToMeta = (name: string, d: SchemaDesc): TFieldMeta => ({
  name,
  label: d.label ?? "",
  required: d.optional === false && d.nullable === false,
  type: d.type ?? "",
  meta: d.meta ?? {},
});

/** Recursively collect field descriptions into a flat map.
 *  Nested array/object fields are keyed WITHOUT indices, e.g. "relatedLinks.label" */
const collectFields = (fields: Record<string, SchemaDesc>, out: Record<string, TFieldMeta>, prefix = "") => {
  for (const key of Object.keys(fields)) {
    const d = fields[key];
    const fullKey = prefix ? `${prefix}.${key}` : key;
    out[fullKey] = descToMeta(fullKey, d);
    try {
      if (d.type === "array" && d.innerType?.type === "object" && d.innerType.fields) {
        collectFields(d.innerType.fields, out, fullKey);
      }
      if (d.type === "object" && d.fields) {
        collectFields(d.fields, out, fullKey);
      }
    } catch {
      /* do nothing */
    }
  }
};

/** Strip array indices: "relatedLinks.0.label" -> "relatedLinks.label" */
const stripIndices = (name: string) => name.replace(/\.\d+(?=\.|$)/g, "");

const FormSchemaProvider: FCC<TFormSchemaProviderProps> = ({ children, schema }) => {
  const fieldsMeta = useMemo(() => {
    const desc = schema.describe();
    if (!("fields" in desc)) return {};
    const result: Record<string, TFieldMeta> = {};
    collectFields((desc as SchemaDesc).fields as Record<string, SchemaDesc>, result);
    return result;
  }, [schema]);

  return <FormSchemaContext.Provider value={fieldsMeta}>{children}</FormSchemaContext.Provider>;
};

export const useFormSchema = () => {
  const meta = useContext(FormSchemaContext);
  return useMemo(
    () =>
      new Proxy(meta, {
        get(target, prop: string | symbol) {
          // Symbol keys (e.g. Symbol.toStringTag, React internals) must pass through untouched
          if (typeof prop !== "string") return Reflect.get(target, prop);
          if (prop in target) return target[prop];
          const normalized = stripIndices(prop);
          if (normalized !== prop && normalized in target) return target[normalized];
          return undefined;
        },
      }),
    [meta],
  );
};
